import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { 
  ClassicFormal, 
  ArtisticCollage,
  ModernMinimal,
  TropicalBeach,
  NoirVinyl,
  LightVinyl,
  PremiumStory,
  RSVP_TEMPLATES,
  COLOR_PRESETS,
  FONT_STYLES
} from '../components/RSVPTemplates';
import EnvelopeAnimation from '../components/EnvelopeAnimation';
import { 
  Save, 
  Eye, 
  Copy, 
  QrCode, 
  Plus, 
  Trash2, 
  Upload,
  ExternalLink,
  Check,
  Image as ImageIcon,
  Palette,
  Type,
  Clock,
  Users,
  CheckCircle,
  XCircle,
  AlertCircle,
  Layout,
  Monitor,
  Smartphone,
  GripVertical,
  X,
  Mail,
  Music,
  Volume2,
  Share2,
  Globe,
  Sparkles
} from 'lucide-react';


// Compress image on the client side before uploading
const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
  return new Promise((resolve) => {
    if (!file.type.startsWith('image/')) { resolve(file); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let w = img.width, h = img.height;
        if (w > maxWidth) { h = Math.round(h * maxWidth / w); w = maxWidth; }
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        canvas.toBlob((blob) => {
          resolve(new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' }));
        }, 'image/jpeg', quality);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

// Hero Layout configurations
const HERO_LAYOUTS = {
  banner: {
    id: 'banner',
    name: 'Banner',
    description: 'Single wide banner image',
    photoCount: 1,
    icon: ({ selected }) => (
      <svg viewBox="0 0 60 40" className={`w-full h-full ${selected ? 'stroke-blue-600' : 'stroke-gray-400'}`} fill="none" strokeWidth="2">
        <rect x="4" y="12" width="52" height="16" rx="1" />
      </svg>
    )
  },
  fullwidth: {
    id: 'fullwidth',
    name: 'Full width',
    description: 'Large full-width image',
    photoCount: 1,
    icon: ({ selected }) => (
      <svg viewBox="0 0 60 40" className={`w-full h-full ${selected ? 'stroke-blue-600' : 'stroke-gray-400'}`} fill="none" strokeWidth="2">
        <rect x="4" y="4" width="52" height="32" rx="1" />
      </svg>
    )
  },
  sidebyside: {
    id: 'sidebyside',
    name: 'Side by side',
    description: 'Two photos side by side',
    photoCount: 2,
    icon: ({ selected }) => (
      <svg viewBox="0 0 60 40" className={`w-full h-full ${selected ? 'stroke-blue-600' : 'stroke-gray-400'}`} fill="none" strokeWidth="2">
        <rect x="4" y="4" width="24" height="32" rx="1" />
        <rect x="32" y="4" width="24" height="32" rx="1" />
      </svg>
    )
  },
  squares: {
    id: 'squares',
    name: 'Squares',
    description: 'Four square photos in a grid',
    photoCount: 4,
    icon: ({ selected }) => (
      <svg viewBox="0 0 60 40" className={`w-full h-full ${selected ? 'stroke-blue-600' : 'stroke-gray-400'}`} fill="none" strokeWidth="2">
        <rect x="6" y="4" width="22" height="14" rx="1" />
        <rect x="32" y="4" width="22" height="14" rx="1" />
        <rect x="6" y="22" width="22" height="14" rx="1" />
        <rect x="32" y="22" width="22" height="14" rx="1" />
      </svg>
    )
  },
  slideshow: {
    id: 'slideshow',
    name: 'Slideshow',
    description: 'Auto-rotating image carousel',
    photoCount: 6,
    minPhotos: 3,
    icon: ({ selected }) => (
      <svg viewBox="0 0 60 40" className={`w-full h-full ${selected ? 'stroke-blue-600' : 'stroke-gray-400'}`} fill="none" strokeWidth="2">
        <rect x="10" y="6" width="40" height="28" rx="1" />
        <rect x="6" y="10" width="4" height="20" rx="1" opacity="0.5" />
        <rect x="50" y="10" width="4" height="20" rx="1" opacity="0.5" />
      </svg>
    )
  },
  marquee: {
    id: 'marquee',
    name: 'Marquee',
    description: 'Scrolling photo strip',
    photoCount: 6,
    minPhotos: 3,
    icon: ({ selected }) => (
      <svg viewBox="0 0 60 40" className={`w-full h-full ${selected ? 'stroke-blue-600' : 'stroke-gray-400'}`} fill="none" strokeWidth="2">
        <rect x="2" y="12" width="14" height="16" rx="1" />
        <rect x="18" y="12" width="14" height="16" rx="1" />
        <rect x="34" y="12" width="14" height="16" rx="1" />
        <rect x="50" y="12" width="14" height="16" rx="1" opacity="0.5" />
      </svg>
    )
  },
  textonly: {
    id: 'textonly',
    name: 'Text only',
    description: 'No hero image, text-focused',
    photoCount: 0,
    icon: ({ selected }) => (
      <svg viewBox="0 0 60 40" className={`w-full h-full ${selected ? 'stroke-blue-600' : 'stroke-gray-400'}`} fill="none" strokeWidth="2">
        <rect x="10" y="10" width="40" height="6" rx="1" />
        <text x="30" y="16" fontSize="6" textAnchor="middle" fill="currentColor" className={selected ? 'fill-blue-600' : 'fill-gray-400'}>ABC</text>
        <rect x="16" y="20" width="28" height="3" rx="0.5" opacity="0.5" />
        <rect x="20" y="26" width="20" height="3" rx="0.5" opacity="0.5" />
      </svg>
    )
  },
  singlepage: {
    id: 'singlepage',
    name: 'Single page',
    description: 'Full background photo with overlay text',
    photoCount: 1,
    icon: ({ selected }) => (
      <svg viewBox="0 0 60 40" className={`w-full h-full ${selected ? 'stroke-blue-600' : 'stroke-gray-400'}`} fill="none" strokeWidth="2">
        <rect x="4" y="4" width="52" height="32" rx="1" />
        <rect x="18" y="16" width="24" height="8" rx="1" fill="currentColor" className={selected ? 'fill-blue-600' : 'fill-gray-400'} opacity="0.3" />
      </svg>
    )
  }
};

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE_URL}/api`;

// Template component map
const TEMPLATE_COMPONENTS = {
  classic_formal: ClassicFormal,
  artistic_collage: ArtisticCollage,
  modern_minimal: ModernMinimal,
  tropical_beach: TropicalBeach,
  noir_vinyl: NoirVinyl,
  light_vinyl: LightVinyl,
  premium_story: PremiumStory,
};

export default function RSVPEditorTab({ eventId, event, token }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rsvpPage, setRsvpPage] = useState(null);
  const [rsvpResponses, setRsvpResponses] = useState([]);
  const [showEnvelopePreview, setShowEnvelopePreview] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [activeSection, setActiveSection] = useState('hero'); // Start with Hero Layout
  const [previewMode, setPreviewMode] = useState('mobile'); // 'mobile' or 'desktop'
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [envelopeUploading, setEnvelopeUploading] = useState({});
  
  const [form, setForm] = useState({
    template: 'classic_formal',
    couple_names: '',
    og_site_name: '',
    event_date: '',
    event_time: '',
    venue_name: '',
    venue_address: '',
    story_text: '',
    timeline: [],
    hero_image_url: '',
    gallery_images: [],
    hero_layout: 'fullwidth', // NEW: Hero layout type
    hero_photos: [], // NEW: Photos specifically for hero section
    color_theme: COLOR_PRESETS[0].colors,
    font_style: 'serif',
    custom_design_url: '',
    rsvp_style: 'below',
    show_countdown: true,
    is_published: false,
    background_music_url: '',
    song_title: '',
    artist_name: '',
    vinyl_cover_image: '',
    hero_bg_image: '',
    content_bg_image: '',
    bg_blur: 5,
    bg_overlay: 10,
    bg_oversize: 25,
    bg_height: 480,
    full_page_bg: false,
    hero_content_y: 0,
    frame_size: 320,
    frame_border: 3,
    frame_border_color: 'rgba(255,255,255,0.2)',
    vinyl_color: '',
    entry_animation: 'none',
    envelope_theme: 'ivory-classic',
    envelope_assets: {},
  });

  useEffect(() => {
    fetchRSVPPage();
    fetchRSVPResponses();
  }, [eventId]);

  const fetchRSVPPage = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/events/${eventId}/rsvp-page`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        setRsvpPage(response.data);
        setForm({
          template: response.data.template || 'classic_formal',
          couple_names: response.data.couple_names || event?.name || '',
          event_date: response.data.event_date || event?.date || '',
          event_time: response.data.event_time || '',
          venue_name: response.data.venue_name || event?.location || '',
          venue_address: response.data.venue_address || '',
          story_text: response.data.story_text || '',
          timeline: response.data.timeline || [],
          hero_image_url: response.data.hero_image_url || '',
          gallery_images: response.data.gallery_images || [],
          hero_layout: response.data.hero_layout || 'fullwidth',
          hero_photos: response.data.hero_photos || [],
          color_theme: response.data.color_theme || COLOR_PRESETS[0].colors,
          font_style: response.data.font_style || 'serif',
          custom_design_url: response.data.custom_design_url || '',
          rsvp_style: response.data.rsvp_style || 'below',
          show_countdown: response.data.show_countdown ?? true,
          is_published: response.data.is_published || false,
          background_music_url: response.data.background_music_url || '',
          song_title: response.data.song_title || '',
          artist_name: response.data.artist_name || '',
          vinyl_cover_image: response.data.vinyl_cover_image || '',
          hero_bg_image: response.data.hero_bg_image || '',
          content_bg_image: response.data.content_bg_image || '',
          bg_blur: response.data.bg_blur ?? 5,
          bg_overlay: response.data.bg_overlay ?? 10,
          bg_oversize: response.data.bg_oversize ?? 25,
          bg_height: response.data.bg_height ?? 480,
          full_page_bg: response.data.full_page_bg ?? false,
          hero_content_y: response.data.hero_content_y ?? 0,
          frame_size: response.data.frame_size ?? 320,
          frame_border: response.data.frame_border ?? 3,
          frame_border_color: response.data.frame_border_color || 'rgba(255,255,255,0.2)',
          vinyl_color: response.data.vinyl_color || '',
          og_site_name: response.data.og_site_name || '',
          notification_email: response.data.notification_email || '',
          entry_animation: response.data.entry_animation || 'none',
          envelope_theme: response.data.envelope_theme || 'ivory-classic',
          envelope_assets: response.data.envelope_assets || {},
        });
      } else {
        // Initialize with event data
        setForm(prev => ({
          ...prev,
          couple_names: event?.name || '',
          event_date: event?.date || '',
          venue_name: event?.location || ''
        }));
      }
    } catch (err) {
      console.error('Error fetching RSVP page:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRSVPResponses = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/rsvp-responses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRsvpResponses(response.data || []);
    } catch (err) {
      console.error('Error fetching RSVP responses:', err);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (rsvpPage) {
        // Update existing
        await axios.put(`${API}/events/${eventId}/rsvp-page`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('RSVP page updated!');
      } else {
        // Create new
        const response = await axios.post(`${API}/events/${eventId}/rsvp-page`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRsvpPage(response.data);
        toast.success('RSVP page created!');
      }
      
      fetchRSVPPage();
    } catch (err) {
      console.error('Error saving RSVP page:', err);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handlePublishToggle = async () => {
    const newPublished = !form.is_published;
    setForm(prev => ({ ...prev, is_published: newPublished }));
    
    if (rsvpPage) {
      try {
        await axios.put(`${API}/events/${eventId}/rsvp-page`, { is_published: newPublished }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(newPublished ? 'Invitation published!' : 'Invitation unpublished');
        fetchRSVPPage();
      } catch (err) {
        console.error('Error toggling publish:', err);
        setForm(prev => ({ ...prev, is_published: !newPublished }));
      }
    }
  };

  const copyLink = () => {
    if (rsvpPage?.slug) {
      const link = `${window.location.origin}/invite/${rsvpPage.slug}`;
      navigator.clipboard.writeText(link);
      toast.success('Invite link copied!');
    }
  };

  const copyShareLink = () => {
    if (rsvpPage?.slug) {
      const link = `${window.location.origin}/api/invite-preview/${rsvpPage.slug}`;
      navigator.clipboard.writeText(link);
      toast.success('Share link copied! Rich preview will show on WhatsApp, iMessage & social media');
    }
  };

  const handleEnvelopeAssetUpload = async (slot, file) => {
    if (!rsvpPage) { toast.error('Save the invitation first'); return; }
    setEnvelopeUploading(prev => ({ ...prev, [slot]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(
        `${API}/events/${eventId}/rsvp-page/upload-envelope-asset?slot=${slot}`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      setForm(prev => ({ ...prev, envelope_assets: res.data.envelope_assets }));
      toast.success(`${slot} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setEnvelopeUploading(prev => ({ ...prev, [slot]: false }));
    }
  };

  const handleEnvelopeAssetDelete = async (slot) => {
    if (!rsvpPage) return;
    try {
      const res = await axios.delete(
        `${API}/events/${eventId}/rsvp-page/envelope-asset/${slot}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setForm(prev => ({ ...prev, envelope_assets: res.data.envelope_assets }));
      toast.success(`${slot} removed`);
    } catch (err) {
      toast.error('Failed to remove asset');
    }
  };

  const addTimelineItem = () => {
    setForm(prev => ({
      ...prev,
      timeline: [...prev.timeline, { time: '', title: '', icon: 'clock' }]
    }));
  };

  const updateTimelineItem = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      timeline: prev.timeline.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeTimelineItem = (index) => {
    setForm(prev => ({
      ...prev,
      timeline: prev.timeline.filter((_, i) => i !== index)
    }));
  };

  // RSVP Stats
  const totalResponses = rsvpResponses.length;
  const confirmedCount = rsvpResponses.filter(r => r.attending).length;
  const declinedCount = rsvpResponses.filter(r => !r.attending).length;
  const totalGuests = rsvpResponses.filter(r => r.attending).reduce((sum, r) => sum + (r.guest_count || 1), 0);
  const duplicateCount = rsvpResponses.filter(r => r.is_duplicate_flag).length;

  // Get template component for preview
  const TemplateComponent = TEMPLATE_COMPONENTS[form.template] || ClassicFormal;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Digital Invitation</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Create a beautiful digital invitation for your guests</p>
        </div>
        
        <div className="flex items-center gap-3">
          {rsvpPage && (
            <>
              <Button variant="outline" size="sm" onClick={copyShareLink} data-testid="copy-share-link" className="text-gray-700 dark:text-gray-200">
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
              <Button variant="outline" size="sm" onClick={copyLink} data-testid="copy-invite-link" className="text-gray-700 dark:text-gray-200">
                <Copy className="w-4 h-4 mr-2" />
                Copy Link
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => window.open(`/invite/${rsvpPage.slug}`, '_blank')}
                data-testid="preview-invite"
                className="text-gray-700 dark:text-gray-200"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Preview
              </Button>
              {user?.is_admin && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => window.open(`/events/${eventId}/instagram-story`, '_blank')}
                  data-testid="instagram-story-btn"
                  className="text-gray-700 dark:text-gray-200"
                >
                  <Monitor className="w-4 h-4 mr-2" />
                  IG Story
                </Button>
              )}
            </>
          )}
          <Button onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {rsvpPage && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <Users className="w-4 h-4" />
              Responses
            </div>
            <p className="text-2xl font-semibold">{totalResponses}</p>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-green-600 text-sm mb-1">
              <CheckCircle className="w-4 h-4" />
              Attending
            </div>
            <p className="text-2xl font-semibold text-green-600">{confirmedCount}</p>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-red-500 text-sm mb-1">
              <XCircle className="w-4 h-4" />
              Declined
            </div>
            <p className="text-2xl font-semibold text-red-500">{declinedCount}</p>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-blue-600 text-sm mb-1">
              <Users className="w-4 h-4" />
              Total Guests
            </div>
            <p className="text-2xl font-semibold text-blue-600">{totalGuests}</p>
          </div>
          {duplicateCount > 0 && (
            <div className="bg-white rounded-lg border p-4 border-amber-200">
              <div className="flex items-center gap-2 text-amber-600 text-sm mb-1">
                <AlertCircle className="w-4 h-4" />
                Duplicates
              </div>
              <p className="text-2xl font-semibold text-amber-600">{duplicateCount}</p>
            </div>
          )}
        </div>
      )}

      {/* Publish Toggle */}
      {rsvpPage && (
        <div className="bg-white rounded-lg border p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Publish Invitation</p>
              <p className="text-sm text-gray-500">
                {form.is_published 
                  ? `Live at: ${window.location.origin}/invite/${rsvpPage.slug}`
                  : 'Your invitation is currently private'}
              </p>
            </div>
            <Switch
              checked={form.is_published}
              onCheckedChange={handlePublishToggle}
            />
          </div>
          
          {/* Notification Email */}
          <div className="flex items-start gap-3 pt-3 border-t">
            <Mail className="w-4 h-4 text-gray-400 mt-2.5" />
            <div className="flex-1">
              <Label className="text-sm font-medium text-gray-700 block mb-1">
                Notification Email
              </Label>
              <Input
                type="email"
                value={form.notification_email || ''}
                onChange={(e) => setForm(prev => ({ ...prev, notification_email: e.target.value }))}
                placeholder={user?.email || 'your@email.com'}
                className="bg-white text-gray-900 border-gray-300"
              />
              <p className="text-xs text-gray-400 mt-1">
                Get notified when a guest RSVPs
              </p>
            </div>
          </div>

          {/* Link Preview Name */}
          <div className="flex items-start gap-3 pt-3 border-t">
            <Globe className="w-4 h-4 text-gray-400 mt-2.5" />
            <div className="flex-1">
              <Label className="text-sm font-medium text-gray-700 block mb-1">
                Link Preview Name
              </Label>
              <Input
                type="text"
                value={form.og_site_name || ''}
                onChange={(e) => setForm(prev => ({ ...prev, og_site_name: e.target.value }))}
                placeholder={form.couple_names || 'e.g. Sarah & James'}
                className="bg-white text-gray-900 border-gray-300"
                data-testid="og-site-name-input"
              />
              <p className="text-xs text-gray-400 mt-1">
                Shown as the site name in WhatsApp, iMessage & social media previews. Defaults to couple names if empty.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Editor Sections - Unified Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left: Editor (3/5 width) */}
        <div className="lg:col-span-3 space-y-6">
          {/* Section Tabs */}
          <div className="flex gap-2 border-b overflow-x-auto">
            {[
              { id: 'hero', label: 'Hero Layout', icon: Layout },
              { id: 'style', label: 'Style', icon: Palette },
              { id: 'content', label: 'Content', icon: Type },
              { id: 'timeline', label: 'Timeline', icon: Clock },
              { id: 'images', label: 'Gallery', icon: ImageIcon },
              { id: 'music', label: 'Music', icon: Music },
              { id: 'animation', label: 'Entry Animation', icon: Sparkles },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 border-b-2 -mb-px transition-colors whitespace-nowrap ${
                  activeSection === tab.id 
                    ? 'border-blue-600 text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Hero Layout Section */}
          {activeSection === 'hero' && (
            <div className="bg-white rounded-lg border p-6 space-y-6">
              {/* Layout Selector */}
              <div>
                <Label className="text-lg font-medium mb-4 block text-gray-800 dark:text-gray-200">Layout</Label>
                <div className="grid grid-cols-4 gap-3">
                  {Object.entries(HERO_LAYOUTS).map(([key, layout]) => {
                    const IconComponent = layout.icon;
                    const isSelected = form.hero_layout === key;
                    return (
                      <button
                        key={key}
                        onClick={() => setForm(prev => ({ ...prev, hero_layout: key }))}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          isSelected
                            ? 'border-blue-600 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`hero-layout-${key}`}
                      >
                        <div className="aspect-[3/2] mb-2 flex items-center justify-center">
                          <IconComponent selected={isSelected} />
                        </div>
                        <p className={`text-xs font-medium text-center ${
                          isSelected ? 'text-blue-600' : 'text-gray-700'
                        }`}>
                          {layout.name}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Photo Requirements */}
              <div className="pt-4 border-t">
                <Label className="text-base font-medium text-gray-800 dark:text-gray-200">
                  Photos {HERO_LAYOUTS[form.hero_layout]?.photoCount === 0 ? '(none needed)' : 
                    `(${HERO_LAYOUTS[form.hero_layout]?.photoCount} required)`}
                </Label>
                <p className="text-sm text-gray-500 mt-1">
                  {HERO_LAYOUTS[form.hero_layout]?.description}
                </p>
              </div>

              {/* Hero Photo Upload Slots */}
              {HERO_LAYOUTS[form.hero_layout]?.photoCount > 0 && (
                <div className="space-y-3">
                  {Array.from({ length: HERO_LAYOUTS[form.hero_layout].photoCount }).map((_, idx) => {
                    const photo = form.hero_photos?.[idx];
                    return (
                      <div 
                        key={idx}
                        className={`flex items-center gap-4 p-4 rounded-lg border-2 ${
                          photo ? 'bg-gray-50 border-gray-200' : 'border-dashed border-gray-300'
                        }`}
                        draggable={!!photo}
                        onDragStart={() => setDraggedIndex(idx)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => {
                          if (draggedIndex !== null && draggedIndex !== idx) {
                            const newPhotos = [...(form.hero_photos || [])];
                            const [moved] = newPhotos.splice(draggedIndex, 1);
                            newPhotos.splice(idx, 0, moved);
                            setForm(prev => ({ ...prev, hero_photos: newPhotos }));
                          }
                          setDraggedIndex(null);
                        }}
                      >
                        {/* Drag Handle */}
                        {photo && (
                          <div className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600">
                            <GripVertical className="w-5 h-5" />
                          </div>
                        )}
                        
                        {/* Photo Thumbnail */}
                        <div className="w-24 h-20 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 border">
                          {photo ? (
                            <img 
                              src={photo.startsWith('/') ? `${BASE_URL}${photo}` : photo}
                              alt={`Hero ${idx + 1}`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <ImageIcon className="w-8 h-8 text-gray-300" />
                            </div>
                          )}
                        </div>
                        
                        {/* Upload Button or Photo Info */}
                        <div className="flex-1 flex items-center justify-between">
                          {photo ? (
                            <>
                              <span className="text-sm font-medium text-gray-700">
                                Photo {idx + 1}
                              </span>
                              <button
                                onClick={() => {
                                  const newPhotos = [...(form.hero_photos || [])];
                                  newPhotos.splice(idx, 1);
                                  setForm(prev => ({ ...prev, hero_photos: newPhotos }));
                                }}
                                className="text-red-500 hover:text-red-700 p-1"
                              >
                                <X className="w-5 h-5" />
                              </button>
                            </>
                          ) : (
                            <label className="cursor-pointer flex items-center gap-2 text-blue-600 hover:text-blue-700">
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  
                                  if (file.size > 10 * 1024 * 1024) {
                                    toast.error('Image too large. Maximum size is 10MB');
                                    return;
                                  }
                                  
                                  try {
                                    const formData = new FormData();
                                    formData.append('file', await compressImage(file));
                                    
                                    const response = await axios.post(
                                      `${API}/events/${eventId}/rsvp-page/upload-image`,
                                      formData,
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    
                                    const newPhotos = [...(form.hero_photos || [])];
                                    newPhotos[idx] = response.data.image_url;
                                    setForm(prev => ({ ...prev, hero_photos: newPhotos }));
                                    toast.success('Photo uploaded!');
                                  } catch (err) {
                                    console.error('Upload error:', err);
                                    toast.error('Failed to upload image');
                                  }
                                }}
                              />
                              <Upload className="w-5 h-5" />
                              <span className="font-medium">Upload</span>
                            </label>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <p className="text-xs text-gray-400 mt-2">Recommended: 1200x1200px square, max 10MB. Images are auto-compressed.</p>
                </div>
              )}

              {form.hero_layout === 'textonly' && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-700">
                    The "Text only" layout displays an elegant text-focused header with no hero image.
                  </p>
                </div>
              )}

              {/* Background Image Upload (Noir Vinyl) */}
              {(form.template === 'noir_vinyl' || form.template === 'light_vinyl') && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-gray-800">Background Image</Label>
                  <p className="text-xs text-gray-500 -mt-2">
                    This image appears blurred behind the hero frame. Falls back to your hero photo if not set.
                  </p>
                  {form.hero_bg_image ? (
                    <div className="flex items-center gap-4">
                      <div className="relative w-24 h-16 rounded-lg overflow-hidden border border-gray-200">
                        <img
                          src={form.hero_bg_image.startsWith('/') ? `${BASE_URL}${form.hero_bg_image}` : form.hero_bg_image}
                          alt="Background"
                          className="w-full h-full object-cover"
                          style={{ filter: 'blur(3px) brightness(0.5)' }}
                        />
                      </div>
                      <button
                        onClick={() => setForm(prev => ({ ...prev, hero_bg_image: '' }))}
                        className="text-sm text-red-500 hover:text-red-600 underline"
                        data-testid="hero-bg-remove"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <label className="block cursor-pointer" data-testid="hero-bg-upload">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 10 * 1024 * 1024) {
                            toast.error('Image too large. Max 10MB.');
                            return;
                          }
                          try {
                            const fd = new FormData();
                            fd.append('file', await compressImage(file));
                            const res = await axios.post(
                              `${API}/events/${eventId}/rsvp-page/upload-image`,
                              fd,
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            setForm(prev => ({ ...prev, hero_bg_image: res.data.image_url }));
                            toast.success('Background image uploaded!');
                          } catch (err) {
                            toast.error('Failed to upload image');
                          }
                        }}
                      />
                      <div className="flex items-center gap-3 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors">
                        <Upload className="w-5 h-5 text-gray-400" />
                        <span className="text-sm text-gray-600">Upload background image</span>
                      </div>
                    </label>
                  )}
                </div>
              )}

              {/* Content Background Image Upload */}
              {(form.template === 'noir_vinyl' || form.template === 'light_vinyl') && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-gray-800">Content Background Image</Label>
                  <p className="text-xs text-gray-500 -mt-2">
                    This image appears behind the content sections (ceremony, story, timeline, RSVP).
                  </p>
                  {form.content_bg_image ? (
                    <div className="flex items-center gap-4">
                      <div className="relative w-24 h-16 rounded-lg overflow-hidden border border-gray-200">
                        <img
                          src={form.content_bg_image.startsWith('/') ? `${BASE_URL}${form.content_bg_image}` : form.content_bg_image}
                          alt="Content Background"
                          className="w-full h-full object-cover"
                          style={{ filter: 'blur(3px) brightness(0.7)' }}
                        />
                      </div>
                      <button
                        onClick={() => setForm(prev => ({ ...prev, content_bg_image: '' }))}
                        className="text-sm text-red-500 hover:text-red-600 underline"
                        data-testid="content-bg-remove"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <label className="block cursor-pointer" data-testid="content-bg-upload">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 10 * 1024 * 1024) {
                            toast.error('Image too large. Max 10MB.');
                            return;
                          }
                          try {
                            const fd = new FormData();
                            fd.append('file', await compressImage(file));
                            const res = await axios.post(
                              `${API}/events/${eventId}/rsvp-page/upload-image`,
                              fd,
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            setForm(prev => ({ ...prev, content_bg_image: res.data.image_url }));
                            toast.success('Content background uploaded!');
                          } catch (err) {
                            toast.error('Failed to upload image');
                          }
                        }}
                      />
                      <div className="flex items-center gap-3 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors">
                        <Upload className="w-5 h-5 text-gray-400" />
                        <span className="text-sm text-gray-600">Upload content background</span>
                      </div>
                    </label>
                  )}
                </div>
              )}

              {/* Size Recommendations */}
              {HERO_LAYOUTS[form.hero_layout]?.photoCount > 0 && (
                <div className="p-3 bg-gray-50 rounded-lg text-sm">
                  <p className="text-gray-600 font-medium mb-1">Recommended sizes:</p>
                  <ul className="text-gray-500 space-y-0.5">
                    {form.hero_layout === 'banner' && <li>Banner: 1920 x 600 px</li>}
                    {form.hero_layout === 'fullwidth' && <li>Full width: 1200 x 800 px</li>}
                    {form.hero_layout === 'sidebyside' && <li>Side by side: 600 x 800 px each</li>}
                    {form.hero_layout === 'squares' && <li>Squares: 600 x 600 px each</li>}
                    {(form.hero_layout === 'slideshow' || form.hero_layout === 'marquee') && <li>800 x 600 px each</li>}
                    {form.hero_layout === 'singlepage' && <li>Single page: 1920 x 1080 px</li>}
                    <li>Maximum file size: 5MB per image</li>
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Style Section */}
          {activeSection === 'style' && (
            <div className="bg-white rounded-lg border p-6 space-y-6">
              <div>
                <Label className="text-sm font-medium mb-3 block text-gray-800 dark:text-gray-200">Choose Template</Label>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(RSVP_TEMPLATES).map(([key, template]) => (
                    <button
                      key={key}
                      onClick={() => {
                        setForm(prev => ({ 
                          ...prev, 
                          template: key,
                          color_theme: template.defaultColors
                        }));
                      }}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        form.template === key 
                          ? 'border-blue-600 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div 
                        className="aspect-[3/4] rounded bg-gray-100 mb-3 flex items-center justify-center"
                        style={{ backgroundColor: template.defaultColors.background }}
                      >
                        <span 
                          className="text-xs font-medium"
                          style={{ color: template.defaultColors.primary }}
                        >
                          {template.name}
                        </span>
                      </div>
                      <p className="font-medium text-sm">{template.name}</p>
                      <p className="text-xs text-gray-500">{template.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Color Theme */}
              <div>
                <Label className="text-sm font-medium mb-3 block text-gray-800 dark:text-gray-200">Color Theme</Label>
                <div className="grid grid-cols-4 gap-2">
                  {COLOR_PRESETS.map((preset, idx) => (
                    <button
                      key={idx}
                      onClick={() => setForm(prev => ({ ...prev, color_theme: preset.colors }))}
                      className={`p-2 rounded border text-center transition-all ${
                        JSON.stringify(form.color_theme) === JSON.stringify(preset.colors)
                          ? 'border-blue-600 ring-2 ring-blue-200'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex justify-center gap-1 mb-1">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: preset.colors.accent }}
                        />
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: preset.colors.primary }}
                        />
                      </div>
                      <span className="text-xs">{preset.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Color Pickers */}
              <div>
                <Label className="text-sm font-medium mb-3 block text-gray-800 dark:text-gray-200">Custom Colors</Label>
                <p className="text-xs text-gray-400 -mt-2 mb-3">Fine-tune individual colors or start from a preset above.</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'background', label: 'Background' },
                    { key: 'primary', label: 'Text' },
                    { key: 'accent', label: 'Accent' },
                    { key: 'secondary', label: 'Secondary' },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 bg-gray-50">
                      <input
                        type="color"
                        value={form.color_theme?.[key] || '#000000'}
                        onChange={(e) => setForm(prev => ({
                          ...prev,
                          color_theme: { ...prev.color_theme, [key]: e.target.value }
                        }))}
                        className="w-8 h-8 rounded cursor-pointer border-0 p-0 bg-transparent"
                        style={{ WebkitAppearance: 'none' }}
                        data-testid={`color-picker-${key}`}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
                        <input
                          type="text"
                          value={form.color_theme?.[key] || '#000000'}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (/^#[0-9a-fA-F]{0,6}$/.test(val)) {
                              setForm(prev => ({
                                ...prev,
                                color_theme: { ...prev.color_theme, [key]: val }
                              }));
                            }
                          }}
                          className="w-full text-xs font-mono bg-transparent border-0 p-0 text-gray-700 focus:outline-none"
                          placeholder="#000000"
                          data-testid={`color-hex-${key}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Font Style */}
              <div>
                <Label className="text-sm font-medium mb-3 block text-gray-800 dark:text-gray-200">Font Style</Label>
                <div className="flex gap-3">
                  {FONT_STYLES.map(font => (
                    <button
                      key={font.id}
                      onClick={() => setForm(prev => ({ ...prev, font_style: font.id }))}
                      className={`flex-1 p-3 rounded border text-center transition-all ${
                        form.font_style === font.id
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className={`text-2xl block mb-1 ${
                        font.id === 'serif' ? 'font-serif' : 
                        font.id === 'sans' ? 'font-sans' : 
                        'font-serif italic'
                      }`}>
                        {font.preview}
                      </span>
                      <span className="text-xs text-gray-600">{font.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Background Image Settings */}
              <div>
                <Label className="text-sm font-medium mb-3 block text-gray-800 dark:text-gray-200">Background Image Settings</Label>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Full Page Background</p>
                      <p className="text-xs text-gray-400">One image covers hero + content</p>
                    </div>
                    <Switch
                      checked={form.full_page_bg}
                      onCheckedChange={(val) => setForm(prev => ({ ...prev, full_page_bg: val }))}
                      data-testid="full-page-bg-toggle"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Hero Height</span>
                      <span>{form.bg_height}px</span>
                    </div>
                    <input
                      type="range"
                      min="300"
                      max="900"
                      step="10"
                      value={form.bg_height}
                      onChange={(e) => setForm(prev => ({ ...prev, bg_height: parseInt(e.target.value) }))}
                      className="w-full accent-blue-600"
                      data-testid="bg-height-slider"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Blur</span>
                      <span>{form.bg_blur}px</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="30"
                      value={form.bg_blur}
                      onChange={(e) => setForm(prev => ({ ...prev, bg_blur: parseInt(e.target.value) }))}
                      className="w-full accent-blue-600"
                      data-testid="bg-blur-slider"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Dark Overlay</span>
                      <span>{form.bg_overlay}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="90"
                      value={form.bg_overlay}
                      onChange={(e) => setForm(prev => ({ ...prev, bg_overlay: parseInt(e.target.value) }))}
                      className="w-full accent-blue-600"
                      data-testid="bg-overlay-slider"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Oversize</span>
                      <span>{form.bg_oversize}px</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="50"
                      value={form.bg_oversize}
                      onChange={(e) => setForm(prev => ({ ...prev, bg_oversize: parseInt(e.target.value) }))}
                      className="w-full accent-blue-600"
                      data-testid="bg-oversize-slider"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Content Position</span>
                      <span>{form.hero_content_y}px</span>
                    </div>
                    <input
                      type="range"
                      min="-150"
                      max="200"
                      step="5"
                      value={form.hero_content_y}
                      onChange={(e) => setForm(prev => ({ ...prev, hero_content_y: parseInt(e.target.value) }))}
                      className="w-full accent-blue-600"
                      data-testid="hero-content-y-slider"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Frame Size</span>
                      <span>{form.frame_size}px</span>
                    </div>
                    <input
                      type="range"
                      min="200"
                      max="500"
                      step="10"
                      value={form.frame_size}
                      onChange={(e) => setForm(prev => ({ ...prev, frame_size: parseInt(e.target.value) }))}
                      className="w-full accent-blue-600"
                      data-testid="frame-size-slider"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Frame Border</span>
                      <span>{form.frame_border}px</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="24"
                      value={form.frame_border}
                      onChange={(e) => setForm(prev => ({ ...prev, frame_border: parseInt(e.target.value) }))}
                      className="w-full accent-blue-600"
                      data-testid="frame-border-slider"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Frame Border Color</span>
                    </div>
                    <input
                      type="color"
                      value={form.frame_border_color?.startsWith('#') ? form.frame_border_color : '#ffffff'}
                      onChange={(e) => setForm(prev => ({ ...prev, frame_border_color: e.target.value }))}
                      className="w-full h-8 rounded cursor-pointer"
                      data-testid="frame-border-color"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Vinyl Record Color</span>
                    </div>
                    <input
                      type="color"
                      value={form.vinyl_color || (form.template === 'light_vinyl' ? '#e8e4df' : '#1a1a1a')}
                      onChange={(e) => setForm(prev => ({ ...prev, vinyl_color: e.target.value }))}
                      className="w-full h-8 rounded cursor-pointer"
                      data-testid="vinyl-color"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Content Section */}
          {activeSection === 'content' && (
            <div className="bg-white rounded-lg border p-6 space-y-4">
              <div>
                <Label className="text-sm font-medium text-gray-700 block mb-1">Couple Names</Label>
                <Input
                  value={form.couple_names}
                  onChange={(e) => setForm(prev => ({ ...prev, couple_names: e.target.value }))}
                  placeholder="John & Jane"
                  className="bg-white text-gray-900 border-gray-300"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700 block mb-1">Event Date</Label>
                  <Input
                    type="date"
                    value={form.event_date}
                    onChange={(e) => setForm(prev => ({ ...prev, event_date: e.target.value }))}
                    className="bg-white text-gray-900 border-gray-300"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700 block mb-1">Event Time</Label>
                  <Input
                    value={form.event_time}
                    onChange={(e) => setForm(prev => ({ ...prev, event_time: e.target.value }))}
                    placeholder="4:00 PM"
                    className="bg-white text-gray-900 border-gray-300"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-700 block mb-1">Venue Name</Label>
                <Input
                  value={form.venue_name}
                  onChange={(e) => setForm(prev => ({ ...prev, venue_name: e.target.value }))}
                  placeholder="Grand Ballroom"
                  className="bg-white text-gray-900 border-gray-300"
                />
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-700 block mb-1">Venue Address</Label>
                <Input
                  value={form.venue_address}
                  onChange={(e) => setForm(prev => ({ ...prev, venue_address: e.target.value }))}
                  placeholder="123 Wedding Lane, City"
                  className="bg-white text-gray-900 border-gray-300"
                />
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-700 block mb-1">Love Story / Message</Label>
                <Textarea
                  value={form.story_text}
                  onChange={(e) => setForm(prev => ({ ...prev, story_text: e.target.value }))}
                  placeholder="Share your story or a special message..."
                  rows={3}
                  className="bg-white text-gray-900 border-gray-300"
                />
              </div>

              {/* Content/Story Photos for Artistic Collage */}
              <div className="pt-4 border-t">
                <Label className="text-sm font-medium text-gray-700 block mb-1">
                  Story Photos
                  <span className="ml-2 text-xs font-normal text-gray-400">
                    (Collage layout in Artistic template)
                  </span>
                </Label>
                <p className="text-xs text-gray-400 mb-3">
                  Photo 1 = small left, Photo 2 = large right (offset down)
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {[0, 1].map((slotIdx) => {
                    const currentImg = form.gallery_images?.[slotIdx] || null;
                    return (
                      <div key={slotIdx}>
                        <p className="text-xs text-gray-500 mb-1 font-medium">
                          {slotIdx === 0 ? 'Left (small)' : 'Right (large)'}
                        </p>
                        {currentImg ? (
                          <div className="relative group">
                            <img
                              src={currentImg.startsWith('/') ? `${BASE_URL}${currentImg}` : currentImg}
                              alt={`Story ${slotIdx + 1}`}
                              className="w-full aspect-[3/4] object-cover rounded-lg shadow"
                              onError={(e) => {
                                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f3f4f6" width="100" height="100"/></svg>';
                              }}
                            />
                            <button
                              onClick={() => {
                                setForm(prev => {
                                  const newGallery = [...(prev.gallery_images || [])];
                                  newGallery[slotIdx] = null;
                                  return { ...prev, gallery_images: newGallery.filter(Boolean) };
                                });
                              }}
                              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <label className="block cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                if (file.size > 10 * 1024 * 1024) {
                                  toast.error('Image too large (max 5MB)');
                                  return;
                                }
                                try {
                                  const formData = new FormData();
                                  formData.append('file', await compressImage(file));
                                  const response = await axios.post(
                                    `${API}/events/${eventId}/rsvp-page/upload-image`,
                                    formData,
                                    { headers: { Authorization: `Bearer ${token}` } }
                                  );
                                  setForm(prev => {
                                    const newGallery = [...(prev.gallery_images || [])];
                                    // Insert at the correct slot position
                                    while (newGallery.length <= slotIdx) newGallery.push(null);
                                    newGallery[slotIdx] = response.data.image_url;
                                    return { ...prev, gallery_images: newGallery.filter(Boolean) };
                                  });
                                  toast.success('Story photo uploaded!');
                                } catch (err) {
                                  toast.error('Upload failed');
                                }
                              }}
                            />
                            <div className="flex flex-col items-center justify-center aspect-[3/4] border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors">
                              <Upload className="w-5 h-5 text-gray-400 mb-1" />
                              <span className="text-xs text-gray-500">Upload</span>
                            </div>
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Timeline Section */}
          {activeSection === 'timeline' && (
            <div className="bg-white rounded-lg border p-6 space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium text-gray-800 dark:text-gray-200">Event Timeline</Label>
                <Button variant="outline" size="sm" onClick={addTimelineItem}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Event
                </Button>
              </div>

              {form.timeline.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-4">
                  No timeline events yet. Add your ceremony schedule!
                </p>
              ) : (
                <div className="space-y-3">
                  {form.timeline.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Input
                        value={item.time}
                        onChange={(e) => updateTimelineItem(idx, 'time', e.target.value)}
                        placeholder="10:00 AM"
                        className="w-28 bg-white text-gray-900 border-gray-300"
                      />
                      <Input
                        value={item.title}
                        onChange={(e) => updateTimelineItem(idx, 'title', e.target.value)}
                        placeholder="Event name"
                        className="flex-1 bg-white text-gray-900 border-gray-300"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTimelineItem(idx)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Images Section */}
          {activeSection === 'images' && (
            <div className="bg-white rounded-lg border p-6 space-y-6">
              {/* Hero Image Upload */}
              <div>
                <Label className="text-sm font-medium mb-2 block text-gray-800 dark:text-gray-200">Hero Image (Main Couple Photo)</Label>
                
                {/* Upload Button */}
                <div className="flex gap-3 mb-3">
                  <label className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        // Check file size
                        if (file.size > 10 * 1024 * 1024) {
                          toast.error('Image too large. Maximum size is 10MB');
                          return;
                        }
                        
                        try {
                          const formData = new FormData();
                          formData.append('file', await compressImage(file));
                          
                          const response = await axios.post(
                            `${API}/events/${eventId}/rsvp-page/upload-image`,
                            formData,
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          
                          setForm(prev => ({ ...prev, hero_image_url: response.data.image_url }));
                          toast.success('Image uploaded!');
                        } catch (err) {
                          console.error('Upload error:', err);
                          toast.error('Failed to upload image');
                        }
                      }}
                    />
                    <div className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                      <Upload className="w-5 h-5 text-gray-400" />
                      <span className="text-sm text-gray-600">Click to upload image</span>
                    </div>
                  </label>
                </div>
                
                {/* Or paste URL */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-xs text-gray-400">or paste URL</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
                
                <Input
                  value={form.hero_image_url}
                  onChange={(e) => setForm(prev => ({ ...prev, hero_image_url: e.target.value }))}
                  placeholder="https://example.com/your-photo.jpg"
                />
                
                {/* Size recommendation */}
                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-700 font-medium mb-1">Recommended Image Size:</p>
                  <ul className="text-xs text-blue-600 space-y-1">
                    <li>• <strong>Classic Formal:</strong> 800 x 1000 px (portrait, 4:5 ratio)</li>
                    <li>• <strong>Artistic Collage:</strong> 1200 x 800 px (landscape, 3:2 ratio)</li>
                    <li>• Maximum file size: 5MB</li>
                    <li>• Formats: JPG, PNG, WebP</li>
                  </ul>
                </div>
              </div>

              {/* Image Preview */}
              {form.hero_image_url && (
                <div>
                  <Label className="text-sm font-medium mb-2 block text-gray-800 dark:text-gray-200">Hero Image Preview</Label>
                  <div className="relative inline-block">
                    <img 
                      src={form.hero_image_url.startsWith('/') ? `${BASE_URL}${form.hero_image_url}` : form.hero_image_url} 
                      alt="Hero Preview" 
                      className="max-w-full max-h-64 rounded-lg shadow-md object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="hidden items-center justify-center w-64 h-40 bg-gray-100 rounded-lg text-gray-400 text-sm">
                      Image failed to load
                    </div>
                    <button
                      onClick={() => setForm(prev => ({ ...prev, hero_image_url: '' }))}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Gallery Images */}
              <div className="pt-6 border-t">
                <Label className="text-sm font-medium mb-3 block text-gray-800 dark:text-gray-200">
                  Gallery Images
                  <span className="ml-2 text-xs font-normal text-gray-400">
                    (Best for Artistic Collage template)
                  </span>
                </Label>
                
                {/* Upload Gallery Images */}
                <div className="flex gap-3 mb-4">
                  <label className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length === 0) return;
                        
                        // Limit to 6 gallery images
                        const currentCount = form.gallery_images?.length || 0;
                        const remainingSlots = 6 - currentCount;
                        
                        if (remainingSlots <= 0) {
                          toast.error('Maximum 6 gallery images allowed');
                          return;
                        }
                        
                        const filesToUpload = files.slice(0, remainingSlots);
                        
                        for (const file of filesToUpload) {
                          if (file.size > 10 * 1024 * 1024) {
                            toast.error(`${file.name} is too large (max 5MB)`);
                            continue;
                          }
                          
                          try {
                            const formData = new FormData();
                            formData.append('file', await compressImage(file));
                            
                            const response = await axios.post(
                              `${API}/events/${eventId}/rsvp-page/upload-image`,
                              formData,
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            
                            setForm(prev => ({
                              ...prev,
                              gallery_images: [...(prev.gallery_images || []), response.data.image_url]
                            }));
                          } catch (err) {
                            console.error('Upload error:', err);
                            toast.error(`Failed to upload ${file.name}`);
                          }
                        }
                        
                        toast.success('Gallery images uploaded!');
                      }}
                    />
                    <div className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                      <Plus className="w-5 h-5 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        Add gallery photos ({form.gallery_images?.length || 0}/6)
                      </span>
                    </div>
                  </label>
                </div>
                
                {/* Gallery Preview Grid */}
                {form.gallery_images && form.gallery_images.length > 0 && (
                  <div className="grid grid-cols-3 gap-3">
                    {form.gallery_images.map((imgUrl, idx) => (
                      <div key={idx} className="relative group aspect-square">
                        <img
                          src={imgUrl.startsWith('/') ? `${BASE_URL}${imgUrl}` : imgUrl}
                          alt={`Gallery ${idx + 1}`}
                          className="w-full h-full object-cover rounded-lg shadow"
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="12">Error</text></svg>';
                          }}
                        />
                        <button
                          onClick={() => {
                            setForm(prev => ({
                              ...prev,
                              gallery_images: prev.gallery_images.filter((_, i) => i !== idx)
                            }));
                          }}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        <span className="absolute bottom-1 left-1 text-xs bg-black/50 text-white px-1.5 py-0.5 rounded">
                          {idx + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                
                <p className="text-xs text-gray-400 mt-3">
                  Gallery images appear as polaroid-style photos in the Artistic Collage template.
                  Recommended size: 400 x 500 px each.
                </p>
              </div>
            </div>
          )}

          {/* ===== Music Section ===== */}
          {activeSection === 'music' && (
            <div className="bg-white rounded-lg border p-6 space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">Vinyl Music Player</h3>
                <p className="text-sm text-gray-500">
                  Add a song that plays as an animated vinyl record on your invitation.
                </p>
              </div>

              {/* Audio File Upload */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-800">Audio File</Label>
                {form.background_music_url ? (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
                    <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full">
                      <Volume2 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-700 truncate">
                        {form.song_title || 'Uploaded audio'}
                      </p>
                      <p className="text-xs text-gray-400">Ready to play</p>
                    </div>
                    <audio 
                      src={form.background_music_url.startsWith('/') ? `${BASE_URL}${form.background_music_url}` : form.background_music_url} 
                      controls 
                      className="h-8 w-32"
                      data-testid="music-audio-preview"
                    />
                    <button
                      onClick={() => setForm(prev => ({ ...prev, background_music_url: '' }))}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                      data-testid="music-remove-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="block cursor-pointer" data-testid="music-upload-label">
                    <input
                      type="file"
                      accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/aac,audio/m4a,audio/x-m4a,audio/mp4"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        if (file.size > 15 * 1024 * 1024) {
                          toast.error('Audio file too large. Maximum size is 15MB.');
                          return;
                        }

                        try {
                          const formData = new FormData();
                          formData.append('file', file);
                          
                          toast.loading('Uploading audio...', { id: 'audio-upload' });
                          
                          const response = await axios.post(
                            `${API}/events/${eventId}/rsvp-page/upload-audio`,
                            formData,
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          
                          setForm(prev => ({ ...prev, background_music_url: response.data.audio_url }));
                          toast.success('Audio uploaded!', { id: 'audio-upload' });
                        } catch (err) {
                          console.error('Audio upload error:', err);
                          toast.error(err.response?.data?.detail || 'Failed to upload audio', { id: 'audio-upload' });
                        }
                      }}
                    />
                    <div className="flex flex-col items-center justify-center gap-2 px-6 py-8 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors">
                      <Upload className="w-8 h-8 text-gray-400" />
                      <span className="text-sm font-medium text-gray-600">Upload audio file</span>
                      <span className="text-xs text-gray-400">MP3, WAV, OGG, AAC, M4A (max 15MB)</span>
                    </div>
                  </label>
                )}
              </div>

              {/* Song Details */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="song_title" className="text-sm font-medium text-gray-800">Song Title</Label>
                  <Input
                    id="song_title"
                    value={form.song_title || ''}
                    onChange={(e) => setForm(prev => ({ ...prev, song_title: e.target.value }))}
                    placeholder="e.g. Perfect"
                    className="mt-1"
                    data-testid="music-song-title"
                  />
                </div>
                <div>
                  <Label htmlFor="artist_name" className="text-sm font-medium text-gray-800">Artist Name</Label>
                  <Input
                    id="artist_name"
                    value={form.artist_name || ''}
                    onChange={(e) => setForm(prev => ({ ...prev, artist_name: e.target.value }))}
                    placeholder="e.g. Ed Sheeran"
                    className="mt-1"
                    data-testid="music-artist-name"
                  />
                </div>
              </div>

              {/* Vinyl Cover / Album Art */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-800">Album Art</Label>
                <p className="text-xs text-gray-500 -mt-2">
                  Custom image for the vinyl label center. Falls back to your hero photo if not set.
                </p>
                {form.vinyl_cover_image ? (
                  <div className="flex items-center gap-4">
                    <div className="relative w-20 h-20 rounded-full overflow-hidden border-2 border-gray-200 shadow-sm">
                      <img
                        src={form.vinyl_cover_image.startsWith('/') ? `${BASE_URL}${form.vinyl_cover_image}` : form.vinyl_cover_image}
                        alt="Album art"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <button
                      onClick={() => setForm(prev => ({ ...prev, vinyl_cover_image: '' }))}
                      className="text-sm text-red-500 hover:text-red-600 underline"
                      data-testid="music-cover-remove"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <label className="block cursor-pointer" data-testid="music-cover-upload">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 10 * 1024 * 1024) {
                          toast.error('Image too large. Max 10MB.');
                          return;
                        }
                        try {
                          const formData = new FormData();
                          formData.append('file', await compressImage(file));
                          const response = await axios.post(
                            `${API}/events/${eventId}/rsvp-page/upload-image`,
                            formData,
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          setForm(prev => ({ ...prev, vinyl_cover_image: response.data.image_url }));
                          toast.success('Album art uploaded!');
                        } catch (err) {
                          toast.error('Failed to upload image');
                        }
                      }}
                    />
                    <div className="flex items-center gap-3 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <ImageIcon className="w-5 h-5 text-gray-400" />
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-600">Upload album art</span>
                        <p className="text-xs text-gray-400">Square image, 400x400px recommended</p>
                      </div>
                    </div>
                  </label>
                )}
              </div>

              <p className="text-xs text-gray-400">
                The vinyl player appears on your invitation between the content and the RSVP form.
                Upload a song and guests can play it while viewing your invitation.
              </p>
            </div>
          )}

          {activeSection === 'animation' && (
            <div className="space-y-6">
              <div>
                <Label className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-3 block">Entry Animation</Label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Choose how your invitation appears when guests first open it.</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { id: 'none', label: 'None', desc: 'Direct — no animation' },
                    { id: 'envelope', label: 'Envelope Open', desc: 'Sealed envelope reveals your invitation' },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => setForm(prev => ({ ...prev, entry_animation: opt.id }))}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        form.entry_animation === opt.id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                      data-testid={`entry-animation-${opt.id}`}
                    >
                      <div className="font-medium text-sm text-gray-800 dark:text-gray-200">{opt.label}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {form.entry_animation === 'envelope' && (
                <>
                  <div>
                    <Label className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-3 block">Envelope Theme</Label>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { id: 'ivory-classic', label: 'Ivory Classic', colors: ['#f5f0e8', '#c4956a', '#d4af37'] },
                        { id: 'ivory-letterpress', label: 'Ivory Letterpress', colors: ['#EFE6DC', '#722F37', '#C6A86B'] },
                        { id: 'blue-floral', label: 'Blue Floral', colors: ['#e8eef5', '#6b8eb5', '#7ba1c7'] },
                        { id: 'sage-garden', label: 'Sage Garden', colors: ['#e8ede6', '#7a9b6d', '#7a9b6d'] },
                        { id: 'dusty-rose', label: 'Dusty Rose', colors: ['#f0e4e4', '#c4858a', '#c4858a'] },
                        { id: 'midnight-gold', label: 'Midnight Gold', colors: ['#2a2a35', '#d4af37', '#d4af37'] },
                      ].map(theme => (
                        <button
                          key={theme.id}
                          onClick={() => setForm(prev => ({ ...prev, envelope_theme: theme.id }))}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            form.envelope_theme === theme.id
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                          }`}
                          data-testid={`envelope-theme-${theme.id}`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            {theme.colors.map((c, i) => (
                              <div key={i} className="w-5 h-5 rounded-full border border-gray-200" style={{ background: c }} />
                            ))}
                          </div>
                          <div className="text-xs font-medium text-gray-700 dark:text-gray-300">{theme.label}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-3 block">Custom Envelope Assets</Label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Upload your own designs to override the default theme. All images should be 1080 x 1440 px (3:4 ratio).</p>
                    <div className="space-y-3">
                      {[
                        { slot: 'base', label: 'Envelope Base', desc: 'Front body of the envelope', required: true },
                        { slot: 'flap', label: 'Flap', desc: 'Triangular top piece (rotates open)', required: true },
                        { slot: 'innerCard', label: 'Inner Card', desc: 'Invitation card visible inside', required: true },
                        { slot: 'seal', label: 'Seal', desc: 'Wax seal or monogram badge', required: false },
                        { slot: 'overlay', label: 'Front Overlay', desc: 'Decorative frame on top of base', required: false },
                        { slot: 'revealBg', label: 'Reveal Background', desc: 'Background revealed during opening', required: false },
                      ].map(({ slot, label, desc, required }) => {
                        const assetUrl = form.envelope_assets?.[slot];
                        const isUploading = envelopeUploading[slot];
                        return (
                          <div
                            key={slot}
                            className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700"
                            data-testid={`envelope-asset-slot-${slot}`}
                          >
                            <div className="w-14 h-14 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex-shrink-0 overflow-hidden flex items-center justify-center">
                              {assetUrl ? (
                                <img src={`${BASE_URL}${assetUrl}`} alt={label} className="w-full h-full object-cover" />
                              ) : (
                                <ImageIcon className="w-5 h-5 text-gray-300 dark:text-gray-600" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                {label}
                                {required && <span className="text-amber-500 ml-1 text-xs">*</span>}
                              </div>
                              <div className="text-xs text-gray-400 dark:text-gray-500 truncate">{desc}</div>
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              {assetUrl && (
                                <button
                                  onClick={() => handleEnvelopeAssetDelete(slot)}
                                  className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-500 transition-colors"
                                  data-testid={`envelope-asset-delete-${slot}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                              <label className={`p-1.5 rounded-md cursor-pointer transition-colors ${
                                isUploading
                                  ? 'opacity-50 pointer-events-none'
                                  : 'hover:bg-blue-50 dark:hover:bg-blue-900/20 text-gray-400 hover:text-blue-500'
                              }`}>
                                {isUploading ? (
                                  <div className="w-3.5 h-3.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <Upload className="w-3.5 h-3.5" />
                                )}
                                <input
                                  type="file"
                                  accept="image/png,image/jpeg,image/webp"
                                  className="hidden"
                                  onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    if (f) handleEnvelopeAssetUpload(slot, f);
                                    e.target.value = '';
                                  }}
                                  disabled={isUploading}
                                  data-testid={`envelope-asset-upload-${slot}`}
                                />
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Right: Unified Live Preview (2/5 width) */}
        <div className="lg:col-span-2">
          <div className="sticky top-4">
            <div className="bg-white rounded-lg border overflow-hidden shadow-lg">
              <div className="p-3 border-b bg-gray-50 flex items-center justify-between">
                <span className="text-sm font-medium">Live Preview</span>
                <div className="flex items-center gap-2">
                  {form.entry_animation === 'envelope' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowEnvelopePreview(true)}
                      className="text-xs h-7"
                      data-testid="preview-envelope-btn"
                    >
                      <Sparkles className="w-3 h-3 mr-1" />
                      Preview Envelope
                    </Button>
                  )}
                  {rsvpPage && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`/invite/${rsvpPage.slug}`, '_blank')}
                      className="text-xs h-7"
                    >
                      <ExternalLink className="w-3 h-3 mr-1" />
                      See Full Page
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Phone Frame Preview */}
              <div className="p-6 bg-gray-100 flex justify-center">
                <div 
                  className="bg-black rounded-[2.5rem] p-2 shadow-xl"
                  style={{ width: '300px' }}
                >
                  {/* Phone notch */}
                  <div className="bg-black h-6 rounded-t-[2rem] flex items-center justify-center">
                    <div className="w-20 h-4 bg-gray-900 rounded-full" />
                  </div>
                  {/* Phone screen */}
                  <div 
                    className="bg-white rounded-b-[2rem] overflow-y-auto relative"
                    style={{ height: '550px' }}
                  >
                    {/* Envelope Preview */}
                    {showEnvelopePreview && form.entry_animation === 'envelope' && (
                      <div className="absolute inset-0 z-50">
                        <EnvelopeAnimation
                          onComplete={() => setShowEnvelopePreview(false)}
                          coupleNames={form.couple_names || ''}
                          eventDate={form.event_date || ''}
                          theme={form.envelope_theme || 'ivory-classic'}
                          customAssets={form.envelope_assets && Object.keys(form.envelope_assets).length > 0
                            ? Object.fromEntries(Object.entries(form.envelope_assets).map(([k, v]) => [k, v?.startsWith('/') ? `${BASE_URL}${v}` : v]))
                            : null
                          }
                          contained={true}
                        />
                      </div>
                    )}
                    <div style={{ zoom: 0.55 }}>
                      <TemplateComponent
                        data={{
                          ...form,
                          hero_image_url: form.hero_image_url?.startsWith('/') 
                            ? `${BASE_URL}${form.hero_image_url}` 
                            : form.hero_image_url,
                          hero_photos: (form.hero_photos || []).map(img => 
                            img?.startsWith('/') ? `${BASE_URL}${img}` : img
                          ).filter(Boolean),
                          gallery_images: (form.gallery_images || []).map(img => 
                            img?.startsWith('/') ? `${BASE_URL}${img}` : img
                          ).filter(Boolean),
                          background_music_url: form.background_music_url?.startsWith('/')
                            ? `${BASE_URL}${form.background_music_url}`
                            : form.background_music_url,
                          vinyl_cover_image: form.vinyl_cover_image?.startsWith('/')
                            ? `${BASE_URL}${form.vinyl_cover_image}`
                            : form.vinyl_cover_image,
                          hero_bg_image: form.hero_bg_image?.startsWith('/')
                            ? `${BASE_URL}${form.hero_bg_image}`
                            : form.hero_bg_image
                        }}
                        isPreview={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="p-3 bg-gray-50 border-t text-center">
                <p className="text-xs text-gray-500">
                  Scroll to see more. Click "See Full Page" to view actual invitation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* RSVP Responses Table */}
      {rsvpResponses.length > 0 && (
        <div className="bg-white rounded-lg border">
          <div className="p-4 border-b">
            <h3 className="font-medium">RSVP Responses</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 text-sm font-medium text-gray-600">Name</th>
                  <th className="text-left p-3 text-sm font-medium text-gray-600">Status</th>
                  <th className="text-left p-3 text-sm font-medium text-gray-600">Guests</th>
                  <th className="text-left p-3 text-sm font-medium text-gray-600">Dietary</th>
                  <th className="text-left p-3 text-sm font-medium text-gray-600">Message</th>
                  <th className="text-left p-3 text-sm font-medium text-gray-600">Date</th>
                </tr>
              </thead>
              <tbody>
                {rsvpResponses.map(response => (
                  <tr key={response.id} className="border-t hover:bg-gray-50">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {response.name}
                        {response.is_duplicate_flag && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                            Possible duplicate
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1 text-sm ${
                        response.attending ? 'text-green-600' : 'text-red-500'
                      }`}>
                        {response.attending ? (
                          <><CheckCircle className="w-4 h-4" /> Attending</>
                        ) : (
                          <><XCircle className="w-4 h-4" /> Declined</>
                        )}
                      </span>
                    </td>
                    <td className="p-3 text-gray-600">{response.guest_count || 1}</td>
                    <td className="p-3 text-gray-600 text-sm">{response.dietary || '-'}</td>
                    <td className="p-3 text-gray-600 text-sm max-w-xs truncate">{response.message || '-'}</td>
                    <td className="p-3 text-gray-500 text-sm">
                      {response.created_at ? new Date(response.created_at).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
